import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import OnboardingStep1 from '../../components/onboarding/OnboardingStep1';
import OnboardingStep2 from '../../components/onboarding/OnboardingStep2';
import OnboardingStep3 from '../../components/onboarding/OnboardingStep3';
import OnboardingStep4 from '../../components/onboarding/OnboardingStep4';
import type { BasicHealthData, DetailedHealthData, UserHealthProfile } from '../../types/health';
import { calculateHealthScore, calculateDataCompleteness } from '../../utils/healthScoring';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [healthData, setHealthData] = useState<Partial<UserHealthProfile>>({});

  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;
  const estimatedTimeLeft = (totalSteps - currentStep) * 0.5; // 30 seconds per step

  useEffect(() => {
    const existingProfile = localStorage.getItem('healthProfile');
    if (existingProfile) {
      navigate('/dashboard');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
      <TopNavigation />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Header */}
        <div className="mb-8 animate-fadeIn">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Health Assessment</h1>
              <p className="text-gray-600 mt-1">
                Question {currentStep} of {totalSteps}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Estimated time remaining</div>
              <div className="text-2xl font-bold text-teal-600">
                {estimatedTimeLeft > 0 ? `< ${Math.ceil(estimatedTimeLeft)} min` : 'Almost done!'}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-teal-500 to-emerald-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`text-xs font-semibold transition-colors ${
                    step <= currentStep ? 'text-teal-600' : 'text-gray-400'
                  }`}
                >
                  Step {step}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 animate-fadeIn">
          {currentStep === 1 && (
            <OnboardingStep1
              onComplete={(data) => {
                setHealthData(prev => ({ ...prev, ...data }));
                setCurrentStep(2);
              }}
            />
          )}

          {currentStep === 2 && (
            <OnboardingStep2
              onComplete={(data) => {
                setHealthData(prev => ({ ...prev, ...data }));
                setCurrentStep(3);
              }}
              onBack={() => setCurrentStep(1)}
            />
          )}

          {currentStep === 3 && (
            <OnboardingStep3
              onComplete={(data) => {
                setHealthData(prev => ({ ...prev, ...data }));
                setCurrentStep(4);
              }}
              onBack={() => setCurrentStep(2)}
            />
          )}

          {currentStep === 4 && (
            <OnboardingStep4
              onComplete={(data) => {
                const completeData = { ...healthData, ...data };
                
                const basicData: BasicHealthData = {
                  age: completeData.age!,
                  biologicalSex: completeData.biologicalSex!,
                  height: completeData.height!,
                  weight: completeData.weight!,
                  activityLevel: completeData.activityLevel!,
                  smokingStatus: completeData.smokingStatus!,
                  alcoholStatus: completeData.alcoholStatus!,
                };

                const detailedData: DetailedHealthData = {
                  nutrition: completeData.nutrition,
                  exercise: completeData.exercise,
                  sleep: completeData.sleep,
                  medicalHistory: {
                    chronicConditions: completeData.chronicConditions || []
                  }
                };

                const score = calculateHealthScore(basicData, detailedData);
                const dataCompleteness = calculateDataCompleteness(basicData, detailedData);

                const profile = {
                  basicData: basicData,
                  detailedData: detailedData,
                  score,
                  dataCompleteness,
                  scoreHistory: [{ date: new Date(), score: score.overall, change: 0 }],
                  createdAt: new Date().toISOString()
                };

                localStorage.setItem('healthProfile', JSON.stringify(profile));
                navigate('/dashboard');
              }}
              onBack={() => setCurrentStep(3)}
            />
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <i className="ri-lock-line mr-1"></i>
          Your data is stored locally and never shared with third parties
        </div>
      </div>
    </div>
  );
}
